import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CalendarBlank, Moon, ShareNetwork, Sun, Trophy, X } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import HowToPlayDrawer from "@/components/HowToPlayDrawer";
import { DIFFICULTY, MAX_TRIES, dayLabel, share, shareText, starText } from "@/lib/daily";

// Bump with page-puzzle.js / jangle.html changes (same idea as Game.jsx JANGLE_BUILD).
const JANGLE_BUILD = "20261105-rack-gap";

// The daily puzzle: the real game board (jangle.html in puzzle mode, see
// public/game/bot/page-puzzle.js) with one turn to reach the blue base.
// /daily is today's; /daily/:n any earlier day from the archive.
export default function Daily() {
  const { n: nParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [showingSolution, setShowingSolution] = useState(false);
  const [missNote, setMissNote] = useState("");
  const [confirmGiveUp, setConfirmGiveUp] = useState(false);
  const iframeRef = useRef(null);
  const dataRef = useRef(null);
  const themeRef = useRef(theme);
  dataRef.current = data;

  // Load the day's puzzle (today's when no number is given).
  useEffect(() => {
    let off = false;
    (async () => {
      try {
        let n = Number(nParam);
        if (!n) n = (await api.get("/daily")).data.today;
        const r = await api.get(`/daily/${n}`);
        if (off) return;
        setData(r.data);
        setShowResult(!!r.data.finished);
        setShowingSolution(false);
      } catch (e) {
        if (!off) setError(formatApiError(e) || "Couldn't load the puzzle");
      }
    })();
    return () => { off = true; };
  }, [nParam]);

  const post = useCallback((msg) => {
    try { iframeRef.current?.contentWindow?.postMessage(msg, "*"); } catch { /* not loaded */ }
  }, []);
  const sendPuzzle = useCallback(() => {
    const d = dataRef.current;
    if (d) post({ type: "jangle:puzzle_load", puzzle: d.puzzle, finished: !!d.finished });
  }, [post]);

  // Messages from the board: ready for the position, and each Play.
  useEffect(() => {
    const onMsg = async (e) => {
      const m = e.data;
      if (!m || typeof m !== "object" || e.source !== iframeRef.current?.contentWindow) return;
      if (m.type === "jangle:puzzle_ready") sendPuzzle();
      if (m.type === "jangle:return_to_lobby") navigate("/lobby");
      if (m.type !== "jangle:puzzle_try") return;
      const d = dataRef.current;
      if (!d || d.finished) return;
      try {
        const r = await api.post(`/daily/${d.n}/try`, { won: !!m.won, tiles: m.tiles || 0, pows: m.pows || 0 });
        setData(r.data);
        if (r.data.finished) setTimeout(() => setShowResult(true), r.data.won ? 900 : 1100);
        else {
          const left = MAX_TRIES - r.data.tries;
          setMissNote(`Not quite, that didn't reach the base. ${left} ${left === 1 ? "try" : "tries"} left.`);
          setTimeout(() => setMissNote(""), 5000);
        }
      } catch (err) {
        toast.error(formatApiError(err) || "Couldn't save that try");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [navigate, sendPuzzle]);

  // The board follows the site theme without reloading.
  useEffect(() => { post({ type: "jangle:theme", theme }); }, [theme, post]);

  const seeSolution = () => {
    if (!data?.solution) return;
    post({ type: "jangle:puzzle_show", solution: data.solution });
    setShowingSolution(true);
    setShowResult(false);
  };
  // Give up: ends the puzzle unsolved and plays the solution straight away.
  // The first tap asks to be sure.
  const giveUp = async () => {
    if (!confirmGiveUp) {
      setConfirmGiveUp(true);
      setTimeout(() => setConfirmGiveUp(false), 4000);
      return;
    }
    setConfirmGiveUp(false);
    try {
      const r = await api.post(`/daily/${data.n}/give-up`);
      setData(r.data);
      if (r.data.solution) {
        post({ type: "jangle:puzzle_show", solution: r.data.solution });
        setShowingSolution(true);
        setShowResult(false);
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't give up just now");
    }
  };
  const onShare = async () => {
    const how = await share(shareText(data));
    if (how === "copied") toast.success("Result copied. Paste it anywhere.");
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-muted-foreground">{error}</p>
        <Button asChild className="rounded-full"><Link to="/daily/archive">Puzzle archive</Link></Button>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-background" />;

  const tries = data.tries || 0;
  // What the puzzle hands you beyond letters, for the goal line.
  const pows = (data.puzzle.pink || 0) + (data.puzzle.green || 0);
  const conn = (data.puzzle.hand || []).includes("~");
  const tools = [pows ? `${pows === 1 ? "a POW" : `${pows} POWs`}` : "", conn ? "a Connector Tile" : ""].filter(Boolean).join(" and ");
  const sol = data.solution || {};
  const solutionText = [
    `${sol.tiles?.length || 0} tiles`,
    sol.pows?.length ? (sol.pows.length === 1 ? "a POW" : `${sol.pows.length} POWs`) : "",
  ].filter(Boolean).join(" and ") + ((sol.tiles || []).some((t) => t[1] === "~") ? " (one is the Connector)" : "");
  const isToday = data.n === data.today;
  const src = `/game/jangle.html?v=${JANGLE_BUILD}&layout=${data.puzzle.layout}&theme=${themeRef.current}` +
    `&mode=turn_based&multiplayer=1&bot=1&puzzle=1&me_name=${encodeURIComponent(user?.username || "You")}&opp_name=Defender`;

  return (
    <div className="h-[100dvh] flex flex-col bg-background text-foreground" data-testid="daily-page">
      <header className="flex-none border-b border-border px-3 md:px-6 py-2 flex items-center gap-2 md:gap-4">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate("/lobby")} aria-label="Lobby" data-testid="daily-back">
          <ArrowLeft size={18} weight="bold" />
        </Button>
        <div className="min-w-0">
          <div className="font-display font-extrabold tracking-tight leading-tight text-base md:text-lg truncate">
            Daily Puzzle #{data.n}
          </div>
          <div className="text-[11px] text-muted-foreground leading-tight">
            {isToday ? "Today" : dayLabel(data.date)} ·{" "}
            <span className="text-amber-400 tracking-wider" title={`${DIFFICULTY[data.difficulty]} puzzle`} data-testid="daily-difficulty">
              {starText(data.difficulty)}
            </span>{" "}
            {DIFFICULTY[data.difficulty]} · Par {data.par} tiles
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1.5" title={`${tries} of ${MAX_TRIES} tries used`} data-testid="daily-tries">
          {Array.from({ length: MAX_TRIES }, (_, k) => (
            <span
              key={k}
              className={`w-3 h-3 rounded-[4px] ${
                k < tries ? (data.won && k === tries - 1 ? "bg-emerald-500" : "bg-destructive") : "bg-muted"
              }`}
            />
          ))}
        </div>
        <Button asChild variant="ghost" size="sm" className="rounded-full gap-1.5" data-testid="daily-archive-link">
          <Link to="/daily/archive">
            <CalendarBlank size={16} weight="bold" />
            <span className="hidden sm:inline">Archive</span>
          </Link>
        </Button>
        <HowToPlayDrawer />
        <Button variant="ghost" size="icon" onClick={toggle} className="rounded-full hidden sm:inline-flex" aria-label="Toggle theme">
          {theme === "dark" ? <Sun size={16} weight="fill" /> : <Moon size={16} weight="fill" />}
        </Button>
      </header>

      {!data.finished && (
        <div
          className={`flex-none px-4 py-2 text-xs md:text-sm text-center border-b transition-colors ${
            missNote ? "bg-destructive/15 border-destructive/30 font-semibold" : "bg-brand/10 border-brand/20"
          }`}
          data-testid="daily-goal"
        >
          {missNote || (
            <>
              Reach the <b className="text-opp">blue base</b> this turn with the letters in your hand
              {tools ? <>, plus <b>{tools}</b></> : ""}. Same puzzle for everyone{isToday ? " today" : ""}.
            </>
          )}
          <button
            className={`ml-2 underline ${confirmGiveUp ? "text-destructive font-semibold" : "text-muted-foreground"}`}
            onClick={giveUp}
            data-testid="daily-give-up"
          >
            {confirmGiveUp ? "Sure? Tap again to see the solution" : "Give up"}
          </button>
        </div>
      )}
      {showingSolution && (
        <div className="flex-none px-4 py-2 text-xs md:text-sm text-center bg-surface border-b border-border flex items-center justify-center gap-3">
          <span>The shortest win the bots found: <b>{solutionText}</b>.</span>
          <button className="underline text-muted-foreground" onClick={seeSolution} data-testid="daily-replay">Replay</button>
          <button className="underline text-muted-foreground" onClick={() => setShowResult(true)}>Back to result</button>
        </div>
      )}

      <div className="flex-1 min-h-0 relative">
        <iframe
          ref={iframeRef}
          key={data.n}
          src={src}
          title="Jangle daily puzzle"
          className="absolute inset-0 w-full h-full border-0"
          data-testid="daily-iframe"
        />

        {showResult && data.finished && (
          <div className="absolute inset-x-0 bottom-0 p-3 md:p-6 flex justify-center pointer-events-none">
            <div className="pointer-events-auto w-full max-w-md bg-surface border border-border rounded-2xl shadow-2xl p-5" data-testid="daily-result">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                    Daily Puzzle #{data.n}
                  </div>
                  <h2 className="font-display text-2xl font-black tracking-tight mt-1">
                    {data.won ? (tries === 1 ? "Solved first try!" : `Solved on try ${tries}`) : data.gave_up ? "You gave up" : "Out of tries"}
                  </h2>
                </div>
                <button className="text-muted-foreground hover:text-foreground p-1" onClick={() => setShowResult(false)} aria-label="Close">
                  <X size={18} weight="bold" />
                </button>
              </div>
              {data.won ? (
                <div className="mt-2 flex items-center gap-3">
                  <span className="text-3xl tracking-wider text-amber-400" data-testid="daily-stars">{starText(data.difficulty)}</span>
                  <span className="text-sm text-muted-foreground">
                    {DIFFICULTY[data.difficulty]} · {data.tiles} tile{data.tiles === 1 ? "" : "s"} · par {data.par}
                  </span>
                </div>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground">
                  <span className="text-amber-400 tracking-wider">{starText(data.difficulty)}</span> {DIFFICULTY[data.difficulty]}.
                  Watch how the bots broke in, then try tomorrow's.
                </p>
              )}
              {data.solved_pct != null && (
                <p className="mt-2 text-xs text-muted-foreground">
                  {data.solved_pct}% of {data.players} player{data.players === 1 ? "" : "s"} solved it.
                </p>
              )}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <Button variant="outline" className="rounded-xl" onClick={seeSolution} data-testid="daily-see-solution">
                  Watch solution
                </Button>
                <Button className="rounded-xl bg-brand hover:bg-brand-hover text-brand-foreground gap-1.5" onClick={onShare} data-testid="daily-share">
                  <ShareNetwork size={16} weight="bold" /> Share
                </Button>
                <Button asChild variant="ghost" className="rounded-xl col-span-2 gap-1.5">
                  <Link to="/daily/archive"><Trophy size={16} weight="duotone" /> All puzzles</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
